import {
  AnalyzeSurveyQuestionInput,
  NewSurvey,
  NewSurveyResponse,
  Question,
  Survey,
  SurveyAIAnalysis,
  SurveyGoogleReviewsConfig,
  SurveyResponse,
  SurveyStatus,
} from "@/types/surveys";
import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/query/react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/firebase";
import { updateOrganization, updateOrganizationSettings } from "@/features/organizations/slice";
import {
  DEFAULT_POST_ORDER_SURVEY,
  extractOrderReview,
} from "@/pages/surveys/surveyTemplates";
import { pick } from "lodash";

export const resolveSurveyStatus = (survey?: Partial<Survey> | null): SurveyStatus => {
  if (!survey) return "draft";
  if (
    survey.status === "draft" ||
    survey.status === "active" ||
    survey.status === "paused" ||
    survey.status === "finished"
  ) {
    return survey.status;
  }
  if (survey.isPublished) {
    return "active";
  }
  const totalCount = survey.totalResponses ?? survey.answers ?? 0;
  if (totalCount > 0 || survey.hasResponses) {
    return "paused";
  }
  return "draft";
};

const ALLOWED_CREATE_FIELDS = [
  "title",
  "description",
  "questions",
  "isPublished",
  "status",
  "trigger",
  "rewardPoints",
  "googleReviews",
  "totalResponses",
  "stats",
  "recentComments",
  "hasResponses",
  "answers",
];

const ALLOWED_DRAFT_UPDATE_FIELDS = [
  "title",
  "description",
  "questions",
  "isPublished",
  "status",
  "trigger",
  "rewardPoints",
  "googleReviews",
];

const ALLOWED_LOCKED_UPDATE_FIELDS = [
  "isPublished",
  "status",
  "title",
  "description",
  "trigger",
  "rewardPoints",
  "googleReviews",
];

export const surveysApi = createApi({
  reducerPath: "surveys",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Survey", "SurveyResponses"],
  endpoints: (builder) => ({
    fetchSurveys: builder.query<Survey[], string>({
      queryFn: async (organizationId: string, { getState }) => {
        try {
          const collectionRef = collection(db, `organizations/${organizationId}/surveys`);
          const querySnapshot = await getDocs(collectionRef);

          const response = await Promise.all(
            querySnapshot.docs.map(async (docSnap) => {
              const data = docSnap.data() as Survey;
              let totalResponses = data.totalResponses ?? data.answers ?? 0;

              // Si la Cloud Function aún no ha actualizado totalResponses, consultamos con .count().get()
              if (totalResponses === 0) {
                try {
                  const responsesRef = collection(
                    db,
                    `organizations/${organizationId}/surveys/${docSnap.id}/responses`
                  );
                  const countSnap = await getCountFromServer(responsesRef);
                  totalResponses = countSnap.data().count;
                } catch {
                  // Ignorar si falla el conteo de respaldo
                }
              }

              const resolvedData: Survey = {
                ...data,
                id: docSnap.id,
                totalResponses,
              };

              return {
                ...resolvedData,
                status: resolveSurveyStatus(resolvedData),
              };
            })
          );

          // Asegurar que exista siempre la encuesta Post-Pedido habilitada por defecto
          if (!response.some((s) => s.trigger === "post_order")) {
            const state = getState() as { auth?: { user?: { uid?: string } } };
            const uid = state.auth?.user?.uid;
            const defaultId = "post_order_default";
            const defaultDraft = {
              title: DEFAULT_POST_ORDER_SURVEY.title || "¿Cómo estuvo tu pedido?",
              description:
                DEFAULT_POST_ORDER_SURVEY.description ||
                "Tu opinión nos ayuda a mejorar cada día.",
              questions: DEFAULT_POST_ORDER_SURVEY.questions || [],
              isPublished: true,
              status: "active" as SurveyStatus,
              trigger: "post_order" as const,
              rewardPoints: 0,
              totalResponses: 0,
              hasResponses: false,
              stats: {},
              recentComments: [],
              answers: 0,
              createdBy: uid || "system",
              createdAt: serverTimestamp(),
              lastUpdatedBy: uid || "system",
              lastUpdatedAt: serverTimestamp(),
            };

            if (uid) {
              try {
                const defaultRef = doc(
                  db,
                  `organizations/${organizationId}/surveys/${defaultId}`
                );
                await setDoc(defaultRef, defaultDraft);
              } catch {
                // Ignorar si el usuario actual no tiene permisos de creación
              }
            }

            response.unshift({
              ...defaultDraft,
              id: defaultId,
            });
          } else {
            // Ordenar para que la encuesta post_order aparezca primero
            response.sort((a, b) => {
              if (a.trigger === "post_order" && b.trigger !== "post_order") return -1;
              if (a.trigger !== "post_order" && b.trigger === "post_order") return 1;
              return (b.totalResponses ?? 0) - (a.totalResponses ?? 0);
            });
          }

          return { data: response };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Survey" as const, id })),
              { type: "Survey", id: "LIST" },
            ]
          : [{ type: "Survey", id: "LIST" }],
    }),

    fetchSurvey: builder.query<
      Survey | undefined,
      {
        organizationId: string | SkipToken;
        surveyId: string | SkipToken;
      }
    >({
      queryFn: async ({ organizationId, surveyId }) => {
        try {
          if (typeof organizationId !== "string" || typeof surveyId !== "string") {
            return { data: undefined };
          }

          const surveyRef = doc(db, `organizations/${organizationId}/surveys/${surveyId}`);
          const surveySnapshot = await getDoc(surveyRef);

          if (!surveySnapshot.exists()) {
            return { data: undefined };
          }

          const rawData = {
            id: surveySnapshot.id,
            ...surveySnapshot.data(),
          } as Survey;

          return {
            data: {
              ...rawData,
              status: resolveSurveyStatus(rawData),
            },
          };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_result, _error, arg) =>
        typeof arg.surveyId === "string"
          ? [{ type: "Survey", id: arg.surveyId }]
          : ["Survey"],
    }),

    fetchPostOrderSurvey: builder.query<
      Survey | null,
      {
        organizationId: string;
        surveyId?: string | null;
      }
    >({
      queryFn: async ({ organizationId }) => {
        try {
          if (!organizationId) {
            return { data: null };
          }

          // Buscar la encuesta única con trigger === "post_order"
          const surveysRef = collection(db, `organizations/${organizationId}/surveys`);
          const q = query(surveysRef, where("trigger", "==", "post_order"));
          const snap = await getDocs(q);

          const candidates = snap.docs
            .map((d) => {
              const rawData = { id: d.id, ...d.data() } as Survey;
              return {
                ...rawData,
                status: resolveSurveyStatus(rawData),
              };
            })
            .filter(
              (s) =>
                s.status === "active" &&
                s.isPublished &&
                (s.questions?.length ?? 0) > 0
            )
            .sort(
              (a, b) =>
                (b.totalResponses ?? b.answers ?? 0) -
                (a.totalResponses ?? a.answers ?? 0)
            );

          if (candidates.length > 0) {
            return { data: candidates[0] };
          }

          return { data: null };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["Survey"],
    }),

    createSurvey: builder.mutation<
      { id: string },
      { organizationId: string; payload: Partial<NewSurvey> }
    >({
      queryFn: async ({ organizationId, payload }, { getState }) => {
        try {
          const state = getState() as { auth?: { user?: { uid?: string } } };
          const uid = state.auth?.user?.uid;
          if (!uid) {
            return { error: new Error("Usuario no autenticado") };
          }

          const sanitized = pick(payload, ALLOWED_CREATE_FIELDS);
          const initialStatus: SurveyStatus =
            sanitized.status ?? (sanitized.isPublished ? "active" : "draft");
          const draft = {
            title: sanitized.title || "Nueva Encuesta",
            description: sanitized.description || "",
            questions: sanitized.questions || [],
            isPublished: initialStatus === "active",
            status: initialStatus,
            trigger: sanitized.trigger || "manual",
            rewardPoints: sanitized.rewardPoints ?? 0,
            totalResponses: 0,
            hasResponses: false,
            stats: {},
            recentComments: [],
            answers: 0,
            createdBy: uid,
            createdAt: serverTimestamp(),
            lastUpdatedBy: uid,
            lastUpdatedAt: serverTimestamp(),
          };

          const collectionRef = collection(db, `organizations/${organizationId}/surveys`);
          const docRef = await addDoc(collectionRef, draft);

          return { data: { id: docRef.id } };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: [{ type: "Survey", id: "LIST" }],
    }),

    duplicateSurvey: builder.mutation<
      { id: string },
      { organizationId: string; survey: Survey }
    >({
      queryFn: async ({ organizationId, survey }, { getState }) => {
        try {
          const state = getState() as { auth?: { user?: { uid?: string } } };
          const uid = state.auth?.user?.uid;
          if (!uid) {
            return { error: new Error("Usuario no autenticado") };
          }

          const draft = {
            title: `${survey.title} (Copia)`,
            description: survey.description || "",
            questions: (survey.questions || []).map((q) => ({
              ...q,
              id: q.id || crypto.randomUUID(),
              options: (q.options || []).map((opt) => ({
                ...opt,
                id: opt.id || crypto.randomUUID(),
              })),
            })),
            isPublished: false,
            status: "draft" as SurveyStatus,
            trigger: survey.trigger || "manual",
            rewardPoints: survey.rewardPoints ?? 0,
            totalResponses: 0,
            hasResponses: false,
            stats: {},
            recentComments: [],
            answers: 0,
            createdBy: uid,
            createdAt: serverTimestamp(),
            lastUpdatedBy: uid,
            lastUpdatedAt: serverTimestamp(),
          };

          const collectionRef = collection(db, `organizations/${organizationId}/surveys`);
          const docRef = await addDoc(collectionRef, draft);

          return { data: { id: docRef.id } };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: [{ type: "Survey", id: "LIST" }],
    }),

    updateSurvey: builder.mutation<
      unknown,
      {
        organizationId: string;
        surveyId: string;
        payload: Partial<Survey>;
        isLocked?: boolean;
      }
    >({
      queryFn: async ({ organizationId, surveyId, payload, isLocked }, { getState }) => {
        try {
          const state = getState() as { auth?: { user?: { uid?: string } } };
          const uid = state.auth?.user?.uid;
          if (!uid) {
            return { error: new Error("Usuario no autenticado") };
          }

          const allowedFields =
            isLocked && payload.trigger !== "post_order"
              ? ALLOWED_LOCKED_UPDATE_FIELDS
              : ALLOWED_DRAFT_UPDATE_FIELDS;

          const picked = pick(payload, allowedFields);
          if (picked.status) {
            picked.isPublished = picked.status === "active";
          } else if (typeof picked.isPublished === "boolean") {
            picked.status = picked.isPublished ? "active" : "draft";
          }

          const draft = {
            ...picked,
            lastUpdatedBy: uid,
            lastUpdatedAt: serverTimestamp(),
          };

          const docRef = doc(db, `organizations/${organizationId}/surveys/${surveyId}`);
          await updateDoc(docRef, draft);
          return { data: { id: surveyId } };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (_result, _error, { surveyId }) => [
        { type: "Survey", id: surveyId },
        { type: "Survey", id: "LIST" },
      ],
    }),

    togglePublishSurvey: builder.mutation<
      unknown,
      { organizationId: string; surveyId: string; isPublished: boolean; status?: SurveyStatus }
    >({
      queryFn: async ({ organizationId, surveyId, isPublished, status }, { getState }) => {
        try {
          const state = getState() as { auth?: { user?: { uid?: string } } };
          const uid = state.auth?.user?.uid;
          if (!uid) {
            return { error: new Error("Usuario no autenticado") };
          }

          const nextStatus: SurveyStatus = status ?? (isPublished ? "active" : "paused");
          const docRef = doc(db, `organizations/${organizationId}/surveys/${surveyId}`);
          await updateDoc(docRef, {
            isPublished: nextStatus === "active",
            status: nextStatus,
            lastUpdatedBy: uid,
            lastUpdatedAt: serverTimestamp(),
          });
          return { data: { id: surveyId, isPublished: nextStatus === "active", status: nextStatus } };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (_result, _error, { surveyId }) => [
        { type: "Survey", id: surveyId },
        { type: "Survey", id: "LIST" },
      ],
    }),

    updateSurveyStatus: builder.mutation<
      { id: string; status: SurveyStatus; isPublished: boolean },
      { organizationId: string; surveyId: string; status: SurveyStatus }
    >({
      queryFn: async ({ organizationId, surveyId, status }, { getState }) => {
        try {
          const state = getState() as { auth?: { user?: { uid?: string } } };
          const uid = state.auth?.user?.uid;
          if (!uid) {
            return { error: new Error("Usuario no autenticado") };
          }

          const isPublished = status === "active";
          const docRef = doc(db, `organizations/${organizationId}/surveys/${surveyId}`);
          await updateDoc(docRef, {
            status,
            isPublished,
            lastUpdatedBy: uid,
            lastUpdatedAt: serverTimestamp(),
          });
          return { data: { id: surveyId, status, isPublished } };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (_result, _error, { surveyId }) => [
        { type: "Survey", id: surveyId },
        { type: "Survey", id: "LIST" },
      ],
    }),

    assignPostOrderSurvey: builder.mutation<
      { postOrderSurveyId: string | null },
      { organizationId: string; surveyId: string | null }
    >({
      queryFn: async ({ organizationId, surveyId }, { getState, dispatch }) => {
        try {
          const state = getState() as {
            auth?: { user?: { uid?: string } };
            organizations?: Record<string, any>;
          };
          const uid = state.auth?.user?.uid;
          if (!uid) {
            return { error: new Error("Usuario no autenticado") };
          }

          const orgRef = doc(db, `organizations/${organizationId}`);
          await updateDoc(orgRef, {
            "settings.postOrderSurveyId": surveyId ?? null,
            lastUpdatedBy: uid,
            lastUpdatedAt: Date.now(),
          });

          const currentSettings = state.organizations?.[organizationId]?.settings ?? {};
          dispatch(
            updateOrganizationSettings({
              organizationId,
              settings: {
                ...currentSettings,
                postOrderSurveyId: surveyId ?? null,
              },
            })
          );

          return { data: { postOrderSurveyId: surveyId ?? null } };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: [{ type: "Survey", id: "LIST" }],
    }),

    saveSurveyGoogleReviewsConfig: builder.mutation<
      {
        surveyId: string;
        googlePlaceId?: string;
        googleReviews: SurveyGoogleReviewsConfig;
      },
      {
        organizationId: string;
        surveyId: string;
        googlePlaceId?: string;
        googleReviews: SurveyGoogleReviewsConfig;
      }
    >({
      queryFn: async (
        { organizationId, surveyId, googlePlaceId, googleReviews },
        { getState, dispatch }
      ) => {
        try {
          const state = getState() as {
            auth?: { user?: { uid?: string } };
            organizations?: Record<string, any>;
          };
          const uid = state.auth?.user?.uid;
          if (!uid) {
            return { error: new Error("Usuario no autenticado") };
          }

          const currentOrg = state.organizations?.[organizationId] ?? {};
          const trimmedPlaceId =
            googlePlaceId !== undefined
              ? googlePlaceId.trim()
              : (currentOrg?.googlePlaceId ?? "");

          if (googlePlaceId !== undefined) {
            await dispatch(
              updateOrganization({
                id: organizationId,
                googlePlaceId: trimmedPlaceId,
              })
            );
          }

          const surveyRef = doc(db, `organizations/${organizationId}/surveys/${surveyId}`);
          await updateDoc(surveyRef, {
            googleReviews,
            lastUpdatedBy: uid,
            lastUpdatedAt: serverTimestamp(),
          });

          return {
            data: {
              surveyId,
              googlePlaceId: trimmedPlaceId,
              googleReviews,
            },
          };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (_result, _error, { surveyId }) => [
        { type: "Survey", id: surveyId },
        { type: "Survey", id: "LIST" },
      ],
    }),

    deleteSurvey: builder.mutation<
      unknown,
      { organizationId: string; surveyId: string }
    >({
      queryFn: async ({ organizationId, surveyId }) => {
        try {
          const docRef = doc(db, `organizations/${organizationId}/surveys/${surveyId}`);
          const snap = await getDoc(docRef);
          if (snap.exists() && snap.data()?.trigger === "post_order") {
            return {
              error: new Error("La encuesta Post-Pedido no puede ser eliminada."),
            };
          }
          await deleteDoc(docRef);
          return { data: { id: surveyId } };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: [{ type: "Survey", id: "LIST" }],
    }),

    /**
     * Verifica si una orden o cliente ya respondió la encuesta usando .count().get()
     * (getCountFromServer) para ahorrar costos de lectura en Firestore.
     */
    checkHasSubmittedSurvey: builder.query<
      boolean,
      {
        organizationId: string;
        surveyId: string;
        orderId?: string;
        clientId?: string;
      }
    >({
      queryFn: async ({ organizationId, surveyId, orderId, clientId }) => {
        try {
          const responsesRef = collection(
            db,
            `organizations/${organizationId}/surveys/${surveyId}/responses`
          );

          if (orderId) {
            const q = query(responsesRef, where("orderId", "==", orderId));
            const countSnap = await getCountFromServer(q);
            return { data: countSnap.data().count > 0 };
          }

          if (clientId) {
            const q = query(responsesRef, where("clientId", "==", clientId));
            const countSnap = await getCountFromServer(q);
            return { data: countSnap.data().count > 0 };
          }

          return { data: false };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["SurveyResponses"],
    }),

    submitSurveyResponse: builder.mutation<
      { id: string },
      {
        organizationId: string;
        surveyId: string;
        survey?: Pick<Survey, "id" | "questions" | "trigger">;
        payload: Omit<NewSurveyResponse, "surveyId" | "organizationId">;
      }
    >({
      queryFn: async ({ organizationId, surveyId, survey, payload }, { getState }) => {
        try {
          const state = getState() as { auth?: { user?: { uid?: string } } };
          const uid = state.auth?.user?.uid;
          const responsesRef = collection(
            db,
            `organizations/${organizationId}/surveys/${surveyId}/responses`
          );

          // Si viene orderId, validamos con .count().get() que no haya respondido ya
          if (payload.orderId) {
            const duplicateQuery = query(
              responsesRef,
              where("orderId", "==", payload.orderId)
            );
            const countSnap = await getCountFromServer(duplicateQuery);
            if (countSnap.data().count > 0) {
              return { error: new Error("Esta orden ya registró una respuesta para esta encuesta.") };
            }
          }

          const draft = {
            surveyId,
            organizationId,
            answers: payload.answers,
            orderId: payload.orderId ?? null,
            orderNumber: payload.orderNumber ?? null,
            branchId: payload.branchId ?? null,
            tableId: payload.tableId ?? null,
            flow: payload.flow ?? null,
            clientId: payload.clientId ?? uid ?? null,
            clientName: payload.clientName ?? null,
            clientPhone: payload.clientPhone ?? null,
            createdBy: uid ?? null,
            createdAt: serverTimestamp(),
          };

          const docRef = await addDoc(responsesRef, draft);

          // Guardar el resumen del review directamente en completedOrders/{orderId}
          if (payload.orderId && survey) {
            try {
              const orderReview = extractOrderReview(
                survey,
                docRef.id,
                payload.answers,
                Date.now()
              );
              const completedOrderRef = doc(
                db,
                `organizations/${organizationId}/completedOrders/${payload.orderId}`
              );
              await updateDoc(completedOrderRef, { orderReview });
            } catch {
              // El trigger onSurveyResponseCreated en Cloud Functions actúa también como respaldo
            }
          }

          return { data: { id: docRef.id } };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (_result, _error, { surveyId }) => [
        { type: "Survey", id: surveyId },
        { type: "Survey", id: "LIST" },
        "SurveyResponses",
      ],
    }),

    fetchSurveyResponses: builder.query<
      SurveyResponse[],
      { organizationId: string; surveyId: string; maxLimit?: number }
    >({
      queryFn: async ({ organizationId, surveyId, maxLimit = 100 }) => {
        try {
          const responsesRef = collection(
            db,
            `organizations/${organizationId}/surveys/${surveyId}/responses`
          );
          const q = query(responsesRef, limit(maxLimit));
          const snapshot = await getDocs(q);

          const getMillis = (val: any): number => {
            if (!val) return 0;
            if (typeof val === "number") return val;
            if (typeof val?.toMillis === "function") return val.toMillis();
            if (typeof val?.seconds === "number") return val.seconds * 1000;
            return 0;
          };

          const rawItems = snapshot.docs.map((docSnap) => {
            const raw = docSnap.data();
            return {
              ...(raw as SurveyResponse),
              id: docSnap.id,
              createdAt: getMillis(raw.createdAt) || undefined,
            };
          });

          // Resolver el número de orden solo si hay respuestas legacy sin orderNumber guardado
          const missingOrderIds = Array.from(
            new Set(
              rawItems
                .filter(
                  (r) =>
                    Boolean(r.orderId) &&
                    (r.orderNumber === undefined ||
                      r.orderNumber === null ||
                      r.orderNumber === "")
                )
                .map((r) => r.orderId as string)
            )
          );

          const orderNumberMap = new Map<string, string | number>();

          if (missingOrderIds.length > 0) {
            await Promise.all(
              missingOrderIds.slice(0, 10).map(async (orderId) => {
                try {
                  const orderRef = doc(
                    db,
                    `organizations/${organizationId}/completedOrders/${orderId}`
                  );
                  const orderSnap = await getDoc(orderRef);
                  if (orderSnap.exists()) {
                    const num = orderSnap.data()?.number;
                    if (num !== undefined && num !== null && num !== "") {
                      orderNumberMap.set(orderId, num);
                    }
                  }
                } catch {
                  // Ignorar si falla la lectura individual
                }
              })
            );
          }

          const data = rawItems
            .map((item) => ({
              ...item,
              orderNumber:
                item.orderNumber !== undefined && item.orderNumber !== null && item.orderNumber !== ""
                  ? item.orderNumber
                  : item.orderId
                    ? (orderNumberMap.get(item.orderId) ?? null)
                    : null,
            }))
            .sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0));

          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["SurveyResponses"],
    }),

    generateSurveyQuestions: builder.mutation<
      { questions: Question[] },
      {
        title: string;
        description?: string;
        trigger?: string;
        existingQuestions?: string[];
        count?: number;
      }
    >({
      queryFn: async (payload) => {
        try {
          const fn = httpsCallable<typeof payload, { questions: Question[] }>(
            functions,
            "generateSurveyQuestions"
          );
          const result = await fn(payload);
          return { data: result.data };
        } catch (error: any) {
          return {
            error: {
              status: 500,
              statusText: error?.message || "Error al generar preguntas con IA",
            },
          };
        }
      },
    }),

    analyzeSurveyResponses: builder.mutation<
      SurveyAIAnalysis,
      {
        title: string;
        description?: string;
        totalResponses: number;
        questions: AnalyzeSurveyQuestionInput[];
      }
    >({
      queryFn: async (payload) => {
        try {
          const fn = httpsCallable<typeof payload, SurveyAIAnalysis>(
            functions,
            "analyzeSurveyResponses"
          );
          const result = await fn(payload);
          return { data: result.data };
        } catch (error: any) {
          const rawMsg = error?.message || "";
          const friendlyMsg =
            !rawMsg || rawMsg.toLowerCase() === "internal"
              ? "Error al analizar las respuestas con IA"
              : rawMsg;
          return {
            error: {
              status: 500,
              statusText: friendlyMsg,
            },
          };
        }
      },
    }),
  }),
});

export const {
  useFetchSurveysQuery,
  useFetchSurveyQuery,
  useFetchPostOrderSurveyQuery,
  useCreateSurveyMutation,
  useDuplicateSurveyMutation,
  useUpdateSurveyMutation,
  useTogglePublishSurveyMutation,
  useUpdateSurveyStatusMutation,
  useAssignPostOrderSurveyMutation,
  useSaveSurveyGoogleReviewsConfigMutation,
  useDeleteSurveyMutation,
  useCheckHasSubmittedSurveyQuery,
  useSubmitSurveyResponseMutation,
  useFetchSurveyResponsesQuery,
  useLazyFetchSurveyResponsesQuery,
  useGenerateSurveyQuestionsMutation,
  useAnalyzeSurveyResponsesMutation,
} = surveysApi;

export default surveysApi;
